import React from 'react';
import Tippy from '@tippyjs/react';
import ActionButton from '../../common/ActionButton';
import { useAuth } from '../../../firebase/useAuth';

const ModalFooter = ({ item, isTopModal, loadingAction, subscriptionStatus, onAdd, onDeleteClick, onSkipClick, onUnskip, onReportClick }) => {
  const { emailVerified } = useAuth();
  const isActionDisabled = !isTopModal || !!loadingAction;
  const isPremiumLocked = item.level > 0 && item.level < 5 && !subscriptionStatus.isSubscribed;
  const isVerificationLocked = !emailVerified;

  const getTooltipContent = () => {
    if (isVerificationLocked) return 'Please verify your email to use this feature.';
    if (isPremiumLocked) return 'Requires a Premium subscription.';
    return null;
  };

  const wrapInTooltip = (button, isDisabled, tooltip) => {
    if (isDisabled && tooltip) {
      return (
        <Tippy content={tooltip} placement="top">
          <span className="cursor-not-allowed">{button}</span>
        </Tippy>
      );
    }
    return button;
  };

  const tooltip = getTooltipContent();

  // Define responsive classes: smaller by default, larger on sm screens and up.
  const buttonClasses = 'w-20 px-2 py-2 text-sm sm:w-24 sm:px-3 sm:text-sm';

  const AddButton = wrapInTooltip(
    <ActionButton
      variant="glassy"
      onClick={onAdd}
      loading={loadingAction === 'add'}
      disabled={isActionDisabled || isPremiumLocked || isVerificationLocked}
      color="green"
      className={buttonClasses} // Use responsive classes
    >
      Add
    </ActionButton>,
    isActionDisabled || isPremiumLocked || isVerificationLocked,
    tooltip || 'Add this word to your learning queue.'
  );

  const SkipButton = wrapInTooltip(
    <ActionButton
      variant="glassy"
      onClick={onSkipClick}
      loading={loadingAction === 'skip'}
      disabled={isActionDisabled || isPremiumLocked || isVerificationLocked}
      color="yellow"
      className={buttonClasses} // Use responsive classes
    >
      Skip
    </ActionButton>,
    isActionDisabled || isPremiumLocked || isVerificationLocked,
    tooltip || 'Skip this word for now.'
  );

  const DeleteButton = wrapInTooltip(
    <ActionButton
      variant="glassy"
      onClick={onDeleteClick}
      loading={loadingAction === 'delete'}
      disabled={isActionDisabled || isPremiumLocked || isVerificationLocked}
      color="red"
      className={buttonClasses} // Use responsive classes
    >
      Delete
    </ActionButton>,
    isActionDisabled || isPremiumLocked || isVerificationLocked,
    tooltip || 'Delete this word from your learning queue.'
  );

  const UnskipButton = wrapInTooltip(
    <ActionButton
      variant="glassy"
      onClick={onUnskip}
      loading={loadingAction === 'unskip'}
      disabled={isActionDisabled || isPremiumLocked || isVerificationLocked}
      color="purple"
      className={`${buttonClasses} text-white glassy-button-neutral`} // Use responsive classes
      isNeutral
    >
      Unskip
    </ActionButton>,
    isActionDisabled || isPremiumLocked || isVerificationLocked,
    tooltip || 'Unskip this word.'
  );

  const ReportButton = (
    <ActionButton
      variant="glassy"
      onClick={onReportClick}
      disabled={isActionDisabled || isPremiumLocked || isVerificationLocked}
      color="neutral"
      className={`${buttonClasses} text-white`} // Use responsive classes
    >
      Report
    </ActionButton>
  );

  return (
    // Make the gap between buttons smaller on xs screens
    <div className="flex-shrink-0 p-4 bg-background-primary border-t-2 border-border flex flex-wrap justify-end items-center gap-1 sm:gap-2">
      {item.isLearned ? DeleteButton : AddButton}
      {item.isLearned && item.skipped ? UnskipButton : SkipButton}
      {ReportButton}
    </div>
  );
};

export default ModalFooter;
